import { useCallback } from "react";
import { useLocation, useParams } from "src/router";

/** Key under which the originating view path is stashed in `location.state`. */
export const CONCEPT_ORIGIN_STATE_KEY = "from" as const;

type ConceptOriginState = {
  [CONCEPT_ORIGIN_STATE_KEY]: string;
};

/**
 * Returns a factory that snapshots the CURRENT location as a workspace-relative
 * path, to attach as `location.state.from` on a concept link. When the concept
 * modal later closes, `useCapturedConceptOrigin` reads it back so the X returns
 * to the exact view the ad was opened from (My ads, Favorites, a folder, a
 * project/chat, etc.).
 *
 * Mirrors the `returnTo` stripping pattern in the lifecycle image editor's
 * `use-svg-editor-session.ts`.
 */
export function useConceptOriginState() {
  const location = useLocation();
  const { root_workspace_slug: workspaceSlug } = useParams<{
    root_workspace_slug?: string;
  }>();

  return useCallback((): ConceptOriginState => {
    const rawPath = location.pathname;
    const strippedPath =
      workspaceSlug && rawPath.startsWith(`/${workspaceSlug}`)
        ? rawPath.slice(`/${workspaceSlug}`.length)
        : rawPath;
    return { [CONCEPT_ORIGIN_STATE_KEY]: strippedPath + location.search };
  }, [location.pathname, location.search, workspaceSlug]);
}

/**
 * Reads back the originating view path stashed by `useConceptOriginState`.
 * Returns undefined when the concept view was reached via a deep link (no
 * captured origin), so callers can fall back to deriving the back path.
 */
export function useCapturedConceptOrigin(): string | undefined {
  const location = useLocation();
  const state = location.state as Partial<ConceptOriginState> | null;
  const from = state?.[CONCEPT_ORIGIN_STATE_KEY];
  return typeof from === "string" ? from : undefined;
}

const CONCEPT_PATH = /\/concepts\//;

/**
 * Like `useConceptOriginState`, for a link that can be followed from inside a
 * concept modal (the command palette). There it keeps the modal's own origin,
 * since capturing the open concept would make close return to itself.
 */
export function useConceptOriginForLink() {
  const captureOrigin = useConceptOriginState();
  const capturedOrigin = useCapturedConceptOrigin();
  const location = useLocation();

  return useCallback((): ConceptOriginState | undefined => {
    if (capturedOrigin) {
      return { [CONCEPT_ORIGIN_STATE_KEY]: capturedOrigin };
    }
    return CONCEPT_PATH.test(location.pathname) ? undefined : captureOrigin();
  }, [captureOrigin, capturedOrigin, location.pathname]);
}
